/**
 * @engine/player - Entry Point
 *
 * This file is the top-level bootstrap used by the HTML5 build
 * and the .NET V8 engine player runtime.
 *
 * It is the single entry-point that esbuild will bundle into `player.js`.
 */

import { IS_ENGINE_PLAYER, hostCanvas } from './env';
import { PlayerApplication } from './PlayerRuntime';

// ---------------------------------------------------------------------------
// DOM helpers (no-op in engine player — getElementById is unavailable)
// ---------------------------------------------------------------------------

function showError(msg: string): void {
  if (!IS_ENGINE_PLAYER) {
    const el = document.getElementById('error');
    if (el) {
      el.textContent = msg;
      el.style.display = 'block';
    }
  }
  console.error('[player]', msg);
}

function setLoading(text: string, pct?: number): void {
  if (IS_ENGINE_PLAYER) return;
  const el = document.getElementById('loading-text');
  if (el) {
    el.textContent = pct !== undefined ? `${text} (${Math.round(pct * 100)}%)` : text;
  }
}

function hideLoading(): void {
  if (IS_ENGINE_PLAYER) return;
  const el = document.getElementById('loading');
  if (el) el.style.display = 'none';
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

async function main(): Promise<void> {
  const cvs = IS_ENGINE_PLAYER
    ? hostCanvas ?? null
    : document.querySelector<HTMLCanvasElement>('#game-canvas');

  if (!cvs) {
    showError('Canvas not found');
    return;
  }

  // Engine player uses defaults; browser reads data-attributes from <body>
  let entryLevel = 'main';
  let assetBase = './assets/';
  let manifest = 'manifest.json';
  let debug = false;

  if (!IS_ENGINE_PLAYER) {
    const body = document.body;
    entryLevel = body.dataset.entryLevel ?? entryLevel;
    assetBase = body.dataset.assetBase ?? assetBase;
    manifest = body.dataset.manifest ?? manifest;
    debug = body.dataset.debug === 'true';
  }

  setLoading('Initializing…');

  const player = new PlayerApplication({
    canvas: cvs,
    entryLevel,
    assetBase,
    manifest,
    debug,
    wasmUrl: './wasm/rapier_wasm2d_bg.wasm',
  });

  (globalThis as unknown as Record<string, unknown>).__player = player;

  try {
    await player.initializeWithProgress((p) => {
      setLoading(p.currentItem ?? p.phase, p.progress);
    });

    setLoading('Loading assets…');
    await player.assets.preloadAll((loaded, total) => {
      setLoading(`Loading assets… ${loaded}/${total}`, loaded / total);
    });

    const resize = (): void => {
      cvs.width = window.innerWidth;
      cvs.height = window.innerHeight;
      player.render.resize(cvs.width, cvs.height);
    };
    resize();
    window.addEventListener('resize', resize);

    hideLoading();
    player.start();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    showError(`Failed to start: ${msg}`);
  }
}

// Kick off once the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void main());
} else {
  void main();
}
